import { useState, useEffect, useCallback } from 'react'
import { postsApi, type PostsPage } from '../api/postsApi'
import type { PostsQueryOptions, CategoryCount } from '../types'

interface UsePostsReturn {
  posts: PostsPage['items']
  total: number
  page: number
  totalPages: number
  loading: boolean
  error: string | null
  setPage: (page: number) => void
  setCategory: (category: string | undefined) => void
  setTag: (tag: string | undefined) => void
  refetch: () => void
}

export function usePosts(initialOptions: PostsQueryOptions = {}): UsePostsReturn {
  const [options, setOptions] = useState<PostsQueryOptions>({
    page: 1,
    pageSize: 10,
    ...initialOptions,
  })
  const [result, setResult] = useState<PostsPage>({
    items: [],
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 1,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    postsApi
      .getAll(options)
      .then((data) => {
        if (!cancelled) {
          setResult(data)
          setLoading(false)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load posts')
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [options, tick])

  const setPage = useCallback((page: number) => {
    setOptions((prev) => ({ ...prev, page }))
  }, [])

  const setCategory = useCallback((category: string | undefined) => {
    setOptions((prev) => ({ ...prev, category, page: 1 }))
  }, [])

  const setTag = useCallback((tag: string | undefined) => {
    setOptions((prev) => ({ ...prev, tag, page: 1 }))
  }, [])

  const refetch = useCallback(() => {
    setTick((t) => t + 1)
  }, [])

  return {
    posts: result.items,
    total: result.total,
    page: result.page,
    totalPages: result.totalPages,
    loading,
    error,
    setPage,
    setCategory,
    setTag,
    refetch,
  }
}

// ─── useCategories ─────────────────────────────────────────────────────────────

interface UseCategoriesReturn {
  categories: CategoryCount[]
  loading: boolean
  error: string | null
}

export function useCategories(): UseCategoriesReturn {
  const [categories, setCategories] = useState<CategoryCount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    postsApi
      .getCategories()
      .then((data) => {
        if (!cancelled) {
          setCategories(data)
          setLoading(false)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load categories')
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { categories, loading, error }
}
